import { useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import { useWorkspaceId } from "src/contexts/global/ApolloSettingsContext";
import { useDesignOptionsEnabled } from "src/pages/Designs/overlay/designOptionsEnabled";
import { getDesignOptions as getDesignOptionsQuery } from "src/pages/Designs/overlay/designOption.graphql";
import useIsDesignWorkspace from "src/pages/Workspaces/components/WorkspaceAccess/useIsDesignWorkspace";
import { getDesignForWorkspace as getDesignForWorkspaceQuery } from "src/pages/Designs/designs.graphql";

export type DesignWorkspaceOption = {
  id: string;
  name: string;
  isTrunk: boolean;
};

export type DesignWorkspaceDesign = {
  id: string;
  designWorkspaceId?: string | null;
  baselineDataDate?: string | null;
  startDate?: string | null;
  targetImplementationDate?: string | null;
  workflowStage?: string | null;
  options?: DesignWorkspaceOption[];
};

/** ponytail: trunk row is DB-backed only after the first overlay create */
const implicitTrunkOption = (): DesignWorkspaceOption => ({
  id: "",
  name: "Current",
  isTrunk: true,
});

const withImplicitTrunk = (
  design: DesignWorkspaceDesign | null | undefined,
  options: DesignWorkspaceOption[] | undefined
): DesignWorkspaceOption[] => {
  if (!design) return [];
  const fetched = options ?? [];
  return fetched.length === 0 ? [implicitTrunkOption()] : fetched;
};

type GetDesignForWorkspaceData = {
  design?: DesignWorkspaceDesign | null;
};

type GetDesignOptionsData = {
  design?: {
    options?: DesignWorkspaceOption[];
  } | null;
};

export type DesignWorkspaceOptionsOverride = {
  design: DesignWorkspaceDesign | null | undefined;
  refetch: () => Promise<unknown>;
};

/**
 * By default resolves the design (and its options) that belongs to the
 * current workspace via getDesignForWorkspace - one server-resolved lookup,
 * not a tenant-wide list matched client-side. Options are fetched only for
 * the matched design via getDesignOptions so FORBIDDEN on other sandboxes
 * cannot kill the switcher. When `override` is supplied the hook instead
 * reflects an already-fetched `design` (and reuses the caller's own
 * `refetch`), skipping both queries entirely.
 */
export const useDesignWorkspaceOptions = (
  override?: DesignWorkspaceOptionsOverride
) => {
  const workspaceId = useWorkspaceId();
  const enabled = useDesignOptionsEnabled();
  const { isDesignWorkspace, loading: isDesignWorkspaceLoading } =
    useIsDesignWorkspace(workspaceId);

  const queryEnabled =
    enabled &&
    isDesignWorkspace &&
    !isDesignWorkspaceLoading &&
    Boolean(workspaceId) &&
    !override;

  const { data, refetch: designRefetch } = useQuery<GetDesignForWorkspaceData>(
    getDesignForWorkspaceQuery,
    {
      skip: !queryEnabled,
      // getDesignForWorkspace takes no GraphQL arguments - the server derives
      // the workspace from the x-workspace-id header - so without this,
      // Apollo's cache key is identical across every workspace: a design
      // cached for one workspace would keep showing (and never refetch) on
      // another. workspaceId isn't declared in the query document, so the
      // server ignores it; it's here purely so Apollo treats each workspace
      // as its own cache entry and refetches when it changes.
      variables: { workspaceId },
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    }
  );

  const matchedDesign = override ? override.design : data?.design;
  const designId = matchedDesign?.id;

  const { data: optionsData, refetch: optionsRefetch } =
    useQuery<GetDesignOptionsData>(getDesignOptionsQuery, {
      skip: !queryEnabled || !designId,
      variables: { id: designId ?? "" },
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
    });

  const optionsLoading =
    queryEnabled && Boolean(designId) && optionsData === undefined;

  const design = useMemo(() => {
    if (override) {
      if (!override.design) return override.design;
      return {
        ...override.design,
        options: withImplicitTrunk(override.design, override.design.options),
      };
    }
    if (!matchedDesign) return undefined;
    return {
      ...matchedDesign,
      options: withImplicitTrunk(matchedDesign, optionsData?.design?.options),
    };
  }, [override, matchedDesign, optionsData]);

  const options: DesignWorkspaceOption[] = design?.options ?? [];

  const refetch = useCallback(async () => {
    if (override) {
      return override.refetch();
    }
    const designResult = await designRefetch();
    const refreshedDesignId = designResult?.data?.design?.id ?? designId;
    if (refreshedDesignId) {
      await optionsRefetch({ id: refreshedDesignId });
    }
  }, [override, designRefetch, optionsRefetch, designId]);

  return {
    enabled,
    isDesignWorkspace,
    // enabled && isDesignWorkspace, computed once here rather than at each
    // of the option-switching callers that only ever gate on this pair.
    active: enabled && isDesignWorkspace,
    workspaceId,
    design,
    options,
    optionsLoading,
    refetch,
  };
};

export default useDesignWorkspaceOptions;
